/**
 * Command grammar — parses a raw input string into a typed intent.
 * Spec 17 P5.1.
 *
 * Vocabulary (matches the variant-c-scenes.jsx prototype's rail):
 *
 *   NAVIGATE:
 *     /spine              → switch to spine view
 *     /timeline           → switch to timeline view
 *     /jump §9.2          → focus a section (accepts §X or CON-401)
 *     /next               → jump to next section needing writer input
 *     /latest             → jump to the most recent event
 *
 *   FILTER (only meaningful in timeline view):
 *     /filter §9.2        → scope timeline to one section's thread
 *     /filter CON-401     → same, internal-id flavour
 *     /filter <kind>      → scope to one EventKind (regen|web|cite|…)
 *     /filter you         → scope to writer-originated events
 *     /filter asthra      → scope to asthra-originated events
 *     /filter clear       → remove all filters
 *     /since break        → filter to events since the last 45m+ gap
 *
 *   ACT (P6; parsed for completeness, no handler wired in P5):
 *     /replay             → P6a walks the timeline
 *     /export             → P6b downloads CSV/JSON of the log
 *     /undo               → P6c reverts the last UI-state action
 *
 * SRP: one pure function ``parseCommand(raw) → CommandIntent | null``.
 * Returns ``null`` for non-command text (free-form chat) — that's the
 * signal to future /ask routing. Caller decides what to do with it.
 *
 * OCP: adding a new command = one entry in the command registry +
 * one case in the parser's dispatcher. Tests pin the grammar; the
 * component layer never parses strings itself.
 *
 * # DECISION: command names are case-insensitive, arguments preserve
 * #   case.
 * # WHY: writers hit ``/Filter`` or ``/FILTER`` as often as ``/filter``;
 * #      being strict would punish muscle-memory without any upside.
 * #      Arguments like ``§9.2`` or ``CON-401`` are real identifiers,
 * #      though, and must preserve case.
 * # DATE: 2026-04-21
 *
 * # DECISION: unknown /commands return ``null``, not an "unknown"
 * #   intent.
 * # WHY: from the writer's POV, a typo in a slash command is
 * #      effectively "I didn't say a command" — the rail should hint
 * #      them toward the valid set rather than dispatching an error
 * #      intent. null keeps the parser's type surface small and
 * #      delegates UX to the rail.
 * # DATE: 2026-04-21
 */

import { EVENT_KINDS, type EventKind } from "../eventLog";

// ---------------------------------------------------------------------------
// Intent types — what the parser emits
// ---------------------------------------------------------------------------

/** Navigate a different surface or focus point. */
export type NavigateIntent =
  | { t: "spine" }
  | { t: "timeline" }
  | { t: "jumpSection"; sectionId: string }
  | { t: "next" }
  | { t: "latest" };

/** Scope the timeline to a subset of events, or clear the scope. */
export type FilterIntent =
  | { t: "section"; sectionId: string }
  | { t: "kind"; kind: EventKind }
  | { t: "actor"; actor: "asthra" | "you" }
  | { t: "sinceBreak" }
  | { t: "clear" };

/**
 * Export format — JSON is the authoritative rich form; CSV is the
 * spreadsheet-friendly flat form. Writers choose by appending the
 * format to the command: ``/export`` defaults to JSON; ``/export csv``
 * emits the flat form.
 */
export type ExportFormat = "json" | "csv";

/** Destructive-ish actions + chat-adjacent shortcuts. */
export type ActIntent =
  | { t: "replay" }
  | { t: "export"; format: ExportFormat }
  | { t: "undo" }
  | { t: "draft" }
  /** Cancel the current run / reset to idle. Equivalent to the
   *  red "stop" affordance the rail used to carry. Mid-run +
   *  approval only; the dispatcher refuses in other states. */
  | { t: "stop" }
  /** Run a web search via the existing ``search_literature``
   *  tool. Args become the query; empty args are accepted and
   *  the agent fills in plausible defaults. */
  | { t: "web"; query: string | null }
  /** Ask the agent to show citations used in the current doc /
   *  active section. Dispatched as a canned chat prompt. */
  | { t: "citations" }
  /** Open the Image Search view — browse the library's figures and
   *  insert one into the document. */
  | { t: "images" }
  /** Build the document's abbreviations table — scans every drafted
   *  section, resolves each abbreviation's full form, and drops a
   *  2-column table into chat for the writer to insert. No args. */
  | { t: "abbreviations" }
  /** Full redraft of the section the cursor is in, via the
   *  proposal-mode pipeline (Approve/Reject card in chat). Args are
   *  the writer's comments — REQUIRED by the dispatcher (the server
   *  rejects an empty instruction); null here so the dispatcher can
   *  return a guidance message instead of a parse failure. */
  | { t: "regen"; guidance: string | null };

/** Picker / selection actions — open the library, workspace, or
 *  mode surfaces. Grouped as its own category so the menu groups
 *  "Pick" items together. */
export type PickIntent =
  | { t: "library" }
  | { t: "workspace"; workspaceId: string | null }
  | { t: "mode"; target: "live" | "sandbox" | null }
  /** Open the library content manager — upload new docs, delete
   *  existing ones. Distinct from /library (which reviews the
   *  classifier's output); /upload changes what's in the library. */
  | { t: "upload" };

/** System-level affordances — settings and session management. */
export type SystemIntent = { t: "settings" } | { t: "logout" };

/** One of the five categories, discriminated by ``kind``. */
export type CommandIntent =
  | { kind: "navigate"; intent: NavigateIntent }
  | { kind: "filter"; intent: FilterIntent }
  | { kind: "act"; intent: ActIntent }
  | { kind: "pick"; intent: PickIntent }
  | { kind: "system"; intent: SystemIntent };

// ---------------------------------------------------------------------------
// Parser
// ---------------------------------------------------------------------------

/**
 * Parse a raw input line into a CommandIntent, or null when the input
 * is empty, not slash-prefixed, or not a recognised command.
 *
 * The parser is split into three helpers — navigate, filter, act —
 * each owning ONE category of the vocabulary. Adding a new command
 * changes exactly one of them.
 */
export function parseCommand(raw: string): CommandIntent | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("/")) return null;
  // Split on first whitespace run to separate command from args.
  // Arguments preserve case; the command token is lowercased.
  const match = /^\/(\S+)(?:\s+(.*))?$/.exec(trimmed);
  if (!match) return null;
  const cmd = match[1].toLowerCase();
  const args = (match[2] ?? "").trim();

  const nav = parseNavigate(cmd, args);
  if (nav) return { kind: "navigate", intent: nav };

  const filter = parseFilter(cmd, args);
  if (filter) return { kind: "filter", intent: filter };

  const act = parseAct(cmd, args);
  if (act) return { kind: "act", intent: act };

  const pick = parsePick(cmd, args);
  if (pick) return { kind: "pick", intent: pick };

  const system = parseSystem(cmd, args);
  if (system) return { kind: "system", intent: system };

  return null;
}

// ---------------------------------------------------------------------------
// Navigate grammar
// ---------------------------------------------------------------------------

function parseNavigate(cmd: string, args: string): NavigateIntent | null {
  switch (cmd) {
    case "spine":
      return { t: "spine" };
    case "timeline":
      return { t: "timeline" };
    case "next":
      return { t: "next" };
    case "latest":
      return { t: "latest" };
    case "jump":
      // /jump §9.2  OR  /jump CON-401 — either is accepted; resolution
      // to the canonical section id happens at the dispatcher (the
      // grammar doesn't know the section list).
      if (!args) return null;
      return { t: "jumpSection", sectionId: normalizeSectionArg(args) };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Filter grammar
// ---------------------------------------------------------------------------

function parseFilter(cmd: string, args: string): FilterIntent | null {
  if (cmd === "since") {
    // /since break — filter to events after the last 45m+ gap.
    if (args.toLowerCase() === "break") return { t: "sinceBreak" };
    return null;
  }
  if (cmd !== "filter") return null;

  if (!args) return null;
  const lower = args.toLowerCase();

  if (lower === "clear") return { t: "clear" };
  if (lower === "you") return { t: "actor", actor: "you" };
  if (lower === "asthra") return { t: "actor", actor: "asthra" };

  // EventKind match — the registry in eventLog.ts is the source of
  // truth. Adding a new kind is one edit there and the grammar picks
  // it up automatically (OCP).
  if ((EVENT_KINDS as readonly string[]).includes(lower)) {
    return { t: "kind", kind: lower as EventKind };
  }

  // Section label — §9.2 or CON-401 or similar. Anything that looks
  // like a section identifier falls here.
  if (looksLikeSection(args)) {
    return { t: "section", sectionId: normalizeSectionArg(args) };
  }

  return null;
}

// ---------------------------------------------------------------------------
// Act grammar — P6 owns the handlers
// ---------------------------------------------------------------------------

function parseAct(cmd: string, args: string): ActIntent | null {
  switch (cmd) {
    case "replay":
      return { t: "replay" };
    case "export": {
      const format = parseExportFormat(args);
      if (format === null) return null;
      return { t: "export", format };
    }
    case "undo":
      return { t: "undo" };
    case "draft":
      // Spec 29 — entry point from the chat. Writer types "/draft"
      // (optionally "/draft document") to open the library + doctype
      // + section picker flow. Args are ignored in v1; a future
      // iteration could accept "/draft CER" as shorthand.
      return { t: "draft" };
    case "stop":
    case "cancel":
      // Single intent under two aliases — "/stop" is the canonical
      // command; "/cancel" is a muscle-memory alias. Both route to
      // the shell's reset-run handler. The dispatcher refuses in
      // states where stopping is meaningless (idle / post-run).
      return { t: "stop" };
    case "web":
    case "internet":
      // Preserve the query arg exactly as typed (case + whitespace
      // meaningful for a literal search). Empty → null so the
      // dispatcher can decide whether to prompt the writer.
      return { t: "web", query: args.length > 0 ? args : null };
    case "citations":
      return { t: "citations" };
    case "images":
    case "image":
    case "figures":
      // Aliases for the Image Search view. "/images" is canonical
      // (writer-facing); the singular + "figures" cover muscle memory.
      return { t: "images" };
    case "abbreviations":
    case "abbreviation":
    case "abbr":
    case "acronyms":
      // Build the abbreviations table. "/abbreviations" is canonical;
      // the shorthand + "acronyms" cover the common muscle-memory typos
      // (incl. the frequent "abberviations" — caught by the menu hint,
      // not here, since we only accept well-formed spellings).
      return { t: "abbreviations" };
    case "regen":
    case "redraft":
      // Preserve the writer's comments exactly as typed (same
      // convention as /web). Empty → null so the dispatcher can ask
      // for comments instead of silently redrafting without guidance.
      return { t: "regen", guidance: args.length > 0 ? args : null };
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Pick grammar — library / workspace / mode
// ---------------------------------------------------------------------------

function parsePick(cmd: string, args: string): PickIntent | null {
  if (cmd === "library") return { t: "library" };
  if (cmd === "upload" || cmd === "files" || cmd === "manage") {
    // Three aliases for the same intent — writers hit whichever
    // word matches their current mental model. "upload" is the
    // primary verb; "files" is the filesystem shorthand; "manage"
    // reads as the verb for curating the library.
    return { t: "upload" };
  }
  if (cmd === "workspace") {
    // /workspace (no args) → open the workspace sub-list
    // /workspace <id-or-name> → set directly (shell resolves)
    return { t: "workspace", workspaceId: args.length > 0 ? args : null };
  }
  if (cmd === "mode") {
    const lower = args.toLowerCase();
    if (lower === "live") return { t: "mode", target: "live" };
    if (lower === "sandbox") return { t: "mode", target: "sandbox" };
    // Bare /mode → toggle. Unknown arg → null (strict; writer
    // mistyped). Empty arg becomes toggle.
    if (lower === "") return { t: "mode", target: null };
    return null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// System grammar — settings / logout
// ---------------------------------------------------------------------------

function parseSystem(cmd: string, _args: string): SystemIntent | null {
  if (cmd === "settings") return { t: "settings" };
  if (cmd === "logout" || cmd === "signout") return { t: "logout" };
  return null;
}

function parseExportFormat(args: string): ExportFormat | null {
  const lower = args.trim().toLowerCase();
  if (lower === "" || lower === "json") return "json";
  if (lower === "csv") return "csv";
  // Unknown format arg — be strict; return null so the rail reports
  // "unknown command" rather than silently defaulting. Writers get
  // told their /export flavor didn't match.
  return null;
}

// ---------------------------------------------------------------------------
// Section-arg helpers
// ---------------------------------------------------------------------------

/**
 * Preserves the section argument as the writer typed it except for
 * trimming surrounding whitespace. ``§9.2`` stays ``§9.2``; ``CON-401``
 * stays ``CON-401`` — resolution is the dispatcher's job.
 */
function normalizeSectionArg(raw: string): string {
  return raw.trim();
}

/**
 * Rough heuristic for "this looks like a section identifier" used by
 * /filter to distinguish section refs from unknown sub-commands. The
 * two accepted shapes per the decisions in Spec 17 §10:
 *   * Starts with ``§``  (writer vocabulary)
 *   * Looks like an ALLCAPS-dash-digits internal id (e.g. ``CON-401``,
 *     ``EFF-201``, ``FRM-603``)
 *
 * False positives here only matter when the dispatcher can't resolve
 * the identifier — which lands the writer at a "no events for §X"
 * empty state, which is already informative. We prefer accepting a
 * writer's typo over rejecting a valid identifier.
 */
function looksLikeSection(raw: string): boolean {
  const t = raw.trim();
  if (t.startsWith("§")) return true;
  return /^[A-Z]+-\d+$/.test(t);
}
